import { Box, Collapse, Grid, List, ListItem, ListItemIcon } from "@mui/material";
import type { JSX, PropsWithChildren } from "react";
import { useCallback, useState } from "react";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import CollapseMediumIcon from "@entrilia/shared/icons/CollapseMediumIcon";
import ExpandMediumIcon from "@entrilia/shared/icons/ExpandMediumIcon";
import GroupFolderIcon from "./GroupFolderIcon.tsx";

interface Props {
  caption: string;
  actions?: JSX.Element;
  defaultExpanded?: boolean;
  dragRef?: (element: HTMLLIElement | null) => void;
  showDragHandle?: boolean;
}

const ExpandableGroupListItem = ({
  caption,
  actions,
  defaultExpanded = false,
  dragRef,
  showDragHandle,
  children,
}: PropsWithChildren<Props>) => {
  const [hover, setHover] = useState(false);
  const [toggled, setToggled] = useState<boolean>();
  const onHover = useCallback(() => setHover(true), []);
  const onLeave = useCallback(() => setHover(false), []);

  const expanded = toggled ?? defaultExpanded;
  const onClick = () => setToggled(!expanded);

  return (
    <>
      <ListItem
        ref={dragRef}
        sx={{
          height: 28,
          p: 0,
          cursor: "pointer",
          backgroundColor: expanded === true ? "action.hover" : "inherit",
          "&:hover": { backgroundColor: "action.hover" },
          borderRadius: "2px",
          pointerEvents: "fill",
        }}
        onMouseEnter={onHover}
        onMouseOver={onHover}
        onMouseLeave={onLeave}
      >
        <Grid container onClick={onClick} sx={{ alignItems: "center", width: "100%", minWidth: 0, flexWrap: "nowrap" }}>
          <ListItemIcon sx={{ minWidth: "20px", width: "20px", color: "secondary.main" }}>
            {expanded ? <CollapseMediumIcon /> : <ExpandMediumIcon />}
          </ListItemIcon>
          <GroupFolderIcon open={expanded} dragHandle={showDragHandle && hover} />
          <TypographyTooltipEllipsis
            observeContainerResize
            text={caption}
            tooltipProps={{ disableInteractive: true }}
            typographyProps={{ variant: "subtitle2", sx: { flexGrow: 1, minWidth: 0 } }}
          />
        </Grid>
        {actions && (
          <Box sx={{ display: "inline-flex", flexShrink: 0, visibility: hover ? "visible" : "hidden" }}>{actions}</Box>
        )}
      </ListItem>
      <Collapse
        orientation="vertical"
        in={expanded}
        mountOnEnter
        unmountOnExit
        timeout={toggled === undefined ? 0 : "auto"}
        sx={{ "& .MuiCollapse-wrapperInner": { display: "block" } }}
      >
        <List sx={{ p: 0, pl: 2, pb: 1 }}>{children}</List>
      </Collapse>
    </>
  );
};

const GroupListItem = (props: PropsWithChildren<Props>) => (
  <ExpandableGroupListItem key={String(props.defaultExpanded ?? false)} {...props} />
);

export default GroupListItem;
